import { useDeferredValue, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Search, UserPlus } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { cn } from "@/lib/cn";
import { Avatar } from "@/components/Avatar";
import { age, formatCpf, formatPhone } from "./PatientListRow";
import { PatientFormDialog } from "./PatientFormDialog";

type Paciente = {
  id: string;
  name: string;
  document: string | null;
  identifier: string | null;
  birthDate: Date | string | null;
  phone: string | null;
  avatarUrl: string | null;
};

/** Data de nascimento é dia, não instante — formatar em UTC evita cair no dia anterior. */
const nascimento = (v: Date | string) => new Date(v).toLocaleDateString("pt-BR", { timeZone: "UTC" });

/**
 * Achar um paciente e ir pra ficha dele — o atalho "Pacientes" do Início.
 *
 * A linha mostra o que distingue homônimos de relance: CPF (ou prontuário), nascimento com idade e
 * telefone. Sem nada digitado, lista os cadastrados mais recentemente. Busca por nome, CPF,
 * telefone, e-mail ou prontuário (a mesma coluna de busca da lista de Pacientes).
 */
export function BuscarPacienteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const podeCadastrar = usePermissao("patients.manage");
  const [busca, setBusca] = useState("");
  const termo = useDeferredValue(busca.trim());
  const [ativo, setAtivo] = useState(0);
  const [cadastrando, setCadastrando] = useState(false);

  useEffect(() => {
    if (open) {
      setBusca("");
      setAtivo(0);
    }
  }, [open]);

  const lista = trpc.patient.list.useQuery(
    termo
      ? { search: termo, active: true, take: 20 }
      : { active: true, take: 12, sortBy: "createdAt", order: "desc" },
    { enabled: open, placeholderData: keepPreviousData },
  );
  const pacientes = (lista.data?.patients ?? []) as Paciente[];
  useEffect(() => setAtivo(0), [termo]);

  const abrir = (id: string) => {
    onClose();
    navigate(`/pacientes/${id}`);
  };

  const teclas = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAtivo((i) => Math.min(i + 1, pacientes.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAtivo((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && pacientes[ativo]) {
      e.preventDefault();
      abrir(pacientes[ativo].id);
    }
  };

  return (
    <>
      <Dialog open={open && !cadastrando} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="flex max-h-[85dvh] max-w-xl flex-col gap-0 overflow-hidden border border-zinc-200 bg-white p-0 shadow-xl dark:border-border dark:bg-card">
          <DialogTitle className="sr-only">Pacientes</DialogTitle>
          <div className="relative border-b border-zinc-100 p-3 pr-12 dark:border-border">
            <Search className="pointer-events-none absolute left-6 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              autoFocus
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={teclas}
              placeholder="Buscar por nome, CPF ou telefone..."
              aria-label="Buscar paciente"
              className="h-11 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-base sm:text-sm dark:border-border dark:bg-card"
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {!termo && pacientes.length > 0 && (
              <p className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/70">
                Cadastrados recentemente
              </p>
            )}
            {lista.isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
              </div>
            ) : pacientes.length === 0 ? (
              <p className="py-10 text-center text-sm text-zinc-500 dark:text-muted-foreground">
                {termo ? "Nenhum paciente encontrado." : "Nenhum paciente cadastrado ainda."}
              </p>
            ) : (
              <ul role="listbox" aria-label="Pacientes">
                {pacientes.map((p, i) => {
                  const idade = age(p.birthDate);
                  const documento = p.document ? `CPF ${formatCpf(p.document)}` : p.identifier ? `Prontuário ${p.identifier}` : null;
                  const detalhes = [
                    documento,
                    p.birthDate ? `${nascimento(p.birthDate)}${idade !== null ? ` · ${idade} anos` : ""}` : null,
                    p.phone ? formatPhone(p.phone) : null,
                  ].filter(Boolean);
                  return (
                    <li key={p.id} role="option" aria-selected={i === ativo}>
                      <button
                        type="button"
                        onClick={() => abrir(p.id)}
                        onMouseEnter={() => setAtivo(i)}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors",
                          i === ativo ? "bg-brand-50 dark:bg-primary/10" : "hover:bg-zinc-50 dark:hover:bg-muted",
                        )}
                      >
                        <Avatar src={p.avatarUrl} name={p.name} size="sm" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{p.name}</span>
                          {detalhes.length > 0 && (
                            <span className="block truncate text-xs text-zinc-500 dark:text-muted-foreground">
                              {detalhes.join("  ·  ")}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Procurou e não achou: cadastrar daqui mesmo, sem voltar e abrir o Agendar consulta. */}
            {termo && !lista.isLoading && pacientes.length === 0 && podeCadastrar && (
              <button
                type="button"
                onClick={() => setCadastrando(true)}
                className="mt-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium text-brand-700 hover:bg-brand-50 dark:text-primary dark:hover:bg-primary/10"
              >
                <UserPlus className="h-4 w-4" />
                Cadastrar "{termo}"
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <PatientFormDialog
        open={cadastrando}
        initialName={termo}
        onClose={() => setCadastrando(false)}
        onCreated={(novo) => {
          setCadastrando(false);
          abrir(novo.id);
        }}
      />
    </>
  );
}
